import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Button,
  Alert,
  Chip,
} from '@mui/material';
import {
  MdPictureAsPdf,
  MdDescription,
  MdDownload,
  MdOpenInNew,
  MdVisibility,
  MdVerifiedUser,
  MdFolderSpecial,
  MdWarningAmber,
  MdAccessTime,
  MdAssessment,
} from 'react-icons/md';
import { jsPDF } from 'jspdf';
import theme, { tierConfig } from './theme';
import { EFFICACY_TERMS } from './efficacyTerminology';
import { API_BASE_URL } from '../utils/api';
import { getAccessToken } from '../utils/auth';
import { getClientEmail } from '../utils/getClientEmail';
import {
  applyLetterPad,
  getLetterPadAssetsDataUrls,
  drawDocumentTitle,
  LAYOUT_MM,
} from '../shared/letterPad';
import EvidencePacksPanel from './reports/EvidencePacksPanel';
import EvidencePackDetailDrawer from './reports/EvidencePackDetailDrawer';
import ExceptionQueuePanel from './reports/ExceptionQueuePanel';
import FollowUpQueuePanel from './reports/FollowUpQueuePanel';
import ComplianceSettingsPanel from './reports/ComplianceSettingsPanel';
import {
  fetchEvidencePacks,
  fetchExceptions,
  fetchFollowUps,
  fetchReports as fetchReportsApi,
  fetchComplianceSettings,
} from '../services/clientReportsApi';
import { can, normalizeHospitalRole } from '../utils/userDashboardPermissions';
import { getUserRole } from '../utils/sessionStore';

const COLORS = theme.colors;

const surfaceCardSx = {
  p: { xs: 2.5, md: 3 },
  borderRadius: `${theme.radius.lg}px`,
  border: `1px solid ${COLORS.borderDefault}`,
  bgcolor: '#fff',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.04)',
};

const outlineBtnSx = {
  textTransform: 'none',
  fontWeight: 600,
  fontSize: 13,
  borderRadius: `${theme.radius.md}px`,
  color: COLORS.textPrimary,
  borderColor: COLORS.borderDefault,
  bgcolor: '#fff',
  py: 0.75,
  transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
  '&:hover': {
    borderColor: COLORS.brandPrimary,
    bgcolor: '#eff6ff',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.12)',
  },
};

const ghostLinkBtnSx = {
  textTransform: 'none',
  fontWeight: 600,
  fontSize: 13,
  color: COLORS.textSecondary,
  borderRadius: `${theme.radius.md}px`,
  '&:hover': { bgcolor: COLORS.bgSecondary, color: COLORS.textPrimary },
};

const exportBtnSx = {
  ...outlineBtnSx,
  justifyContent: 'flex-start',
  px: 2,
  py: 1.35,
  border: `1px solid ${COLORS.borderDefault}`,
  fontWeight: 500,
};

function ReportStat({ icon, label, value, accent }) {
  return (
    <Box
      sx={{
        flex: '1 1 140px',
        minWidth: 140,
        p: 2,
        borderRadius: `${theme.radius.md}px`,
        bgcolor: 'rgba(255,255,255,0.72)',
        border: `1px solid ${COLORS.borderDefault}`,
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: accent || 'rgba(59, 130, 246, 0.1)',
          color: COLORS.brandPrimary,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: COLORS.textSecondary,
          }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: COLORS.textPrimary, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

function formatReportMeta(kpis = {}) {
  return `${kpis.hoursThisMonth ?? '—'} h · ${kpis.sessions ?? '—'} sessions · ${kpis.roomsCovered ?? '—'} rooms`;
}

/** Map live `/client/reports` swab rows (before/after/location) onto export column names. */
function normalizeSwabForExport(t = {}) {
  const before = t.bacterialPre ?? t.before ?? '—';
  const after = t.bacterialPost ?? t.after ?? '—';
  const fungalPre = t.fungalPre ?? '—';
  const fungalPost = t.fungalPost ?? '—';
  const surface = t.surface || t.location || t.category || '—';
  let status = t.status;
  if (!status) {
    if (t.reductionPct != null && Number(t.reductionPct) < 50) status = 'residual';
    else if (t.reductionPct != null) status = 'cleared';
    else status = 'recorded';
  }
  return {
    ...t,
    date: t.date || '—',
    room: t.room || '—',
    surface,
    bacterialPre: before,
    bacterialPost: after,
    fungalPre,
    fungalPost,
    status,
  };
}

function mapApiReportRow(r, index) {
  const kpis = r.kpis || {};
  const month = r.month || r.id || `report-${index}`;
  return {
    id: r.id || month || String(index),
    month,
    title: r.title || `${month} Performance Report`,
    meta: r.meta || formatReportMeta(kpis),
    tag: r.tag || null,
    pdfUrl: r.pdfUrl || null,
    isLatest: Boolean(r.isLatest),
  };
}

function csvEscape(value) {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function buildWordHtml(report, hospitalName, options = {}) {
  const overdueRoomDays = Number(options.overdueRoomDays) > 0 ? Number(options.overdueRoomDays) : 14;
  const c = {
    brandPrimary: theme.colors.brandPrimary,
    brandDark: theme.colors.brandDark,
    success: theme.colors.accentSuccess,
    warning: theme.colors.accentWarning,
    danger: theme.colors.accentDanger,
    info: theme.colors.accentInfo,
    bgSecondary: theme.colors.bgSecondary,
    bgTertiary: theme.colors.bgTertiary,
    textPrimary: theme.colors.textPrimary,
    textSecondary: theme.colors.textSecondary,
    border: theme.colors.borderDefault,
  };

  const condColor = (type) => {
    if (type === 'success' || type === 'win') return c.success;
    if (type === 'warning' || type === 'attention') return c.warning;
    if (type === 'danger' || type === 'error') return c.danger;
    if (type === 'info' || type === 'informational') return c.info;
    return c.textSecondary;
  };

  const condBg = (type) => {
    if (type === 'success' || type === 'win') return '#E8F8F2';
    if (type === 'warning' || type === 'attention') return '#FDF2EC';
    if (type === 'danger' || type === 'error') return '#FAEAEA';
    if (type === 'info' || type === 'informational') return '#EBF2FA';
    return c.bgSecondary;
  };

  const kpis = report.kpis || {};
  const briefing = report.briefing || {};
  const highlights = briefing.highlights || [];
  const sessions = report.sessions || [];
  const rooms = report.rooms || [];
  const swabTests = (report.swabTests || []).map(normalizeSwabForExport);
  const plan = report.plan || {};
  const overdueRooms = report.overdueRooms || [];
  const tierCeiling = plan.tier ? (tierConfig[plan.tier]?.hours || 36) : 36;

  let html = `<html><head><meta charset="utf-8"><title>Monthly Report - ${hospitalName}</title>
<style>
  @page { margin: 20mm; }
  body { font-family: 'Segoe UI', Arial, sans-serif; margin: 24px; color: ${c.textPrimary}; line-height: 1.6; }
  h1 { color: ${c.brandPrimary}; font-size: 22px; margin-bottom: 4px; }
  h2 { color: ${c.brandDark}; font-size: 17px; margin-top: 28px; border-bottom: 2px solid ${c.brandPrimary}; padding-bottom: 6px; }
  h3 { color: ${c.textPrimary}; font-size: 14px; margin-top: 18px; }
  .subtitle { color: ${c.textSecondary}; font-size: 13px; margin-bottom: 20px; }
  .cover-letter { background: ${c.bgTertiary}; border-left: 4px solid ${c.brandPrimary}; padding: 16px; margin: 16px 0; border-radius: 0 8px 8px 0; font-size: 13px; }
  table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 12px; }
  th { background: ${c.brandDark}; color: #fff; padding: 10px 12px; text-align: left; font-weight: 600; }
  td { padding: 8px 12px; border-bottom: 1px solid ${c.border}; }
  tr:nth-child(even) { background: ${c.bgSecondary}; }
  .kpi-grid { display: flex; flex-wrap: wrap; gap: 12px; margin: 16px 0; }
  .kpi-card { background: ${c.bgSecondary}; border: 1px solid ${c.border}; border-radius: 8px; padding: 14px 18px; min-width: 140px; flex: 1; }
  .kpi-value { font-size: 24px; font-weight: 700; color: ${c.brandPrimary}; }
  .kpi-label { font-size: 11px; color: ${c.textSecondary}; text-transform: uppercase; letter-spacing: 0.04em; font-weight: 600; }
  .highlight-box { border-radius: 8px; padding: 12px 16px; margin: 8px 0; font-size: 12px; }
  .highlight-number { display: inline-block; width: 22px; height: 22px; border-radius: 50%; color: #fff; font-weight: 700; font-size: 12px; text-align: center; line-height: 22px; margin-right: 8px; }
  .highlight-type { font-size: 10px; font-weight: 700; text-transform: uppercase; margin-right: 8px; }
  .status-cleared { color: ${c.success}; font-weight: 600; }
  .status-residual { color: ${c.danger}; font-weight: 600; }
  .status-clean { color: ${c.success}; font-weight: 600; }
  .overdue { color: ${c.warning}; }
  .healthy { color: ${c.success}; }
  .pre-value { color: ${c.danger}; font-weight: 600; }
  .post-value { color: ${c.success}; font-weight: 600; }
  .method-auto { color: ${c.success}; font-weight: 600; }
  .method-manual { color: ${c.info}; font-weight: 600; }
  .usage-bar-container { height: 10px; background: ${c.bgSecondary}; border-radius: 5px; margin: 6px 0; overflow: hidden; }
  .usage-bar { height: 100%; border-radius: 5px; }
  .footer { margin-top: 40px; border-top: 2px solid ${c.border}; padding-top: 12px; font-size: 11px; color: ${c.textSecondary}; text-align: center; }
</style></head><body>`;

  html += `<div style="text-align:center;margin-bottom:24px;">
    <div style="width:48px;height:48px;border-radius:10px;background:linear-gradient(135deg,${c.brandPrimary},${c.brandDark});margin:0 auto 10px;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:22px;">S</div>
    <h1>Spotless AI Monthly Report</h1>
    <p class="subtitle">${hospitalName || 'Client Hospital'} &middot; ${report.month || new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</p>
  </div>`;

  if (briefing.coverLetter) {
    html += `<div class="cover-letter">${briefing.coverLetter}</div>`;
  }

  html += `<h2>Key Performance Indicators</h2>
  <div class="kpi-grid">
    <div class="kpi-card"><div class="kpi-label">UVC Hours</div><div class="kpi-value">${kpis.hoursThisMonth ?? '—'}h</div></div>
    <div class="kpi-card"><div class="kpi-label">Sessions</div><div class="kpi-value">${kpis.sessions ?? '—'}</div></div>
    <div class="kpi-card"><div class="kpi-label">Rooms Covered</div><div class="kpi-value">${kpis.roomsCovered ?? '—'}</div></div>
    <div class="kpi-card"><div class="kpi-label">Tier Usage</div><div class="kpi-value">${kpis.tierUsagePct ?? '—'}%</div></div>
    <div class="kpi-card"><div class="kpi-label">Efficacy</div><div class="kpi-value">${kpis.efficacyPct ?? '—'}%</div></div>
    <div class="kpi-card"><div class="kpi-label">Object completion</div><div class="kpi-value">${kpis.avgObjectCompletionRate ?? '—'}%</div></div>
    <div class="kpi-card"><div class="kpi-label">Open follow-ups</div><div class="kpi-value">${kpis.openFollowUpCount ?? '—'}</div></div>
    <div class="kpi-card"><div class="kpi-label">Follow-up closure</div><div class="kpi-value">${kpis.followUpClosureRate ?? '—'}%</div></div>
    <div class="kpi-card"><div class="kpi-label">Pack coverage</div><div class="kpi-value">${kpis.packCoveragePct ?? '—'}%</div></div>
    <div class="kpi-card"><div class="kpi-label">Orphan cycles (30d)</div><div class="kpi-value">${kpis.orphanedCycleCount ?? '—'}</div></div>
    <div class="kpi-card"><div class="kpi-label">Packs under 2 min</div><div class="kpi-value">${kpis.packsUnder2MinPct ?? '—'}%</div></div>
    <div class="kpi-card"><div class="kpi-label">Follow-up SLA met</div><div class="kpi-value">${kpis.followUpSlaMetPct ?? '—'}%</div></div>
    <div class="kpi-card"><div class="kpi-label">Verification attach</div><div class="kpi-value">${kpis.verificationAttachRatePct ?? '—'}%</div></div>
  </div>`;

  html += `<h2>Monthly Briefing</h2>
  <p style="font-size:13px;">${briefing.narrative || 'No briefing data available.'}</p>`;

  if (highlights.length > 0) {
    highlights.forEach((h, i) => {
      const color = condColor(h.type);
      const bg = condBg(h.type);
      const typeLabel = h.type === 'success' || h.type === 'win' ? 'WIN' : h.type === 'warning' || h.type === 'attention' ? 'ATTENTION' : h.type === 'info' ? 'INFO' : 'ALERT';
      html += `<div class="highlight-box" style="background:${bg};border-left:4px solid ${color};">
        <span class="highlight-number" style="background:${color};">${i + 1}</span>
        <span class="highlight-type" style="color:${color};">${typeLabel}</span>
        ${h.text}
      </div>`;
    });
  }

  html += `<h2>Subscription Status</h2>
  <table>
    <tr><td style="font-weight:600;width:40%;">Current Tier</td><td style="color:${c.brandPrimary};font-weight:700;">${plan.tier || '—'}</td></tr>
    <tr><td style="font-weight:600;">Hours Used / Ceiling</td><td>${plan.hoursUsed ?? 0}h / ${tierCeiling}h</td></tr>
    <tr><td style="font-weight:600;">Renewal Date</td><td>${plan.renewalDate || '—'}</td></tr>
    <tr><td style="font-weight:600;">Status</td><td>${plan.status || '—'}</td></tr>
  </table>
  <div class="usage-bar-container">
    <div class="usage-bar" style="width:${Math.min(100, ((plan.hoursUsed || 0) / tierCeiling) * 100)}%;background:${(plan.hoursUsed || 0) / tierCeiling > 0.9 ? c.danger : c.brandPrimary};"></div>
  </div>`;

  if (overdueRooms.length > 0) {
    html += `<h2 style="color:${c.warning};">⚠ Rooms Requiring Attention</h2>
    <table><tr><th>Room</th><th>Type</th><th>Days Since Last Disinfection</th></tr>`;
    overdueRooms.forEach((r) => {
      html += `<tr><td class="overdue" style="font-weight:600;">${r.name}</td><td>${r.type || '—'}</td><td class="overdue">${r.daysSince ?? '—'} days</td></tr>`;
    });
    html += `</table>`;
  }

  if (sessions.length > 0) {
    html += `<h2>Session Log (Latest ${Math.min(sessions.length, 50)})</h2>
    <table><tr><th>Date</th><th>Room</th><th>Type</th><th>Duration</th><th>Method</th></tr>`;
    sessions.slice(0, 50).forEach((s) => {
      const methodClass = s.method === 'Auto' ? 'method-auto' : 'method-manual';
      html += `<tr><td>${s.date}</td><td style="font-weight:600;">${s.room}</td><td>${s.type || '—'}</td><td>${s.duration}</td><td class="${methodClass}">${s.method}</td></tr>`;
    });
    html += `</table>`;
  }

  if (rooms.length > 0) {
    html += `<h2>Room Status Summary</h2>
    <table><tr><th>Room</th><th>Type</th><th>Total Sessions</th><th>Days Since Last</th><th>Status</th></tr>`;
    rooms.forEach((r) => {
      const isOverdue = r.daysSinceLastDisinfection > overdueRoomDays;
      const statusClass = isOverdue ? 'overdue' : 'healthy';
      const statusLabel = isOverdue ? 'Overdue' : 'Healthy';
      html += `<tr><td style="font-weight:600;">${r.name}</td><td>${r.type || '—'}</td><td>${r.totalSessions ?? 0}</td><td class="${statusClass}">${r.daysSinceLastDisinfection ?? '—'}</td><td class="${statusClass}" style="font-weight:600;">${statusLabel}</td></tr>`;
    });
    html += `</table>`;
  }

  if (swabTests.length > 0) {
    html += `<h2>Environmental Swab Results (room-scoped)</h2>
    <table><tr><th>Date</th><th>Room</th><th>Surface</th><th>Bacterial (Pre)</th><th>Bacterial (Post)</th><th>Fungal (Pre)</th><th>Fungal (Post)</th><th>Status</th></tr>`;
    swabTests.forEach((t) => {
      const statusClass = t.status === 'residual' ? 'status-residual' : 'status-cleared';
      const statusLabel =
        t.status === 'residual' ? EFFICACY_TERMS.postUvcPositive.reportsStatus : t.status;
      html += `<tr>
        <td>${t.date}</td><td style="font-weight:600;">${t.room}</td><td>${t.surface || '—'}</td>
        <td class="pre-value">${t.bacterialPre ?? '—'}</td><td class="post-value">${t.bacterialPost ?? '—'}</td>
        <td class="pre-value">${t.fungalPre ?? '—'}</td><td class="post-value">${t.fungalPost ?? '—'}</td>
        <td class="${statusClass}" style="text-transform:capitalize;">${statusLabel}</td>
      </tr>`;
    });
    html += `</table>`;
  }

  html += `<div class="footer">
    <p>Generated by Spotless AI Dashboard &middot; ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
    <p style="color:${c.brandPrimary};">Confidential — For ${hospitalName || 'Client'} internal use only. For hospital audits and insurance documentation.</p>
    <p style="font-size:10px;">Automated UV-C room disinfection is an adjunct to manual cleaning. This report is supporting evidence for the organisation’s IPC programme and does not replace manual cleaning.</p>
    <p style="font-size:10px;">This report documents disinfection activity and related hygiene evidence. It does not claim NABH or JCI accreditation compliance or product endorsement.</p>
  </div></body></html>`;

  return html;
}

/** Shared PDF helpers used by monthly report + certificate + swab exports */
function createPdfPalette() {
  return {
    brandPrimary: [59, 130, 246],
    brandDark: [37, 99, 235],
    successColor: [5, 150, 105],
    warningColor: [217, 119, 6],
    dangerColor: [220, 38, 38],
    infoColor: [37, 99, 235],
    textPrimary: [15, 23, 42],
    textSecondary: [71, 85, 105],
  };
}

function writePdfSectionTitle(pdf, title, margin, pageW, y, brandDark, brandPrimary) {
  pdf.setFontSize(12);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(...brandDark);
  pdf.text(title, margin, y);
  y += 3;
  pdf.setDrawColor(...brandPrimary);
  pdf.setLineWidth(0.7);
  pdf.line(margin, y, margin + Math.min(55, pageW - 2 * margin), y);
  return y + 7;
}

function writePdfTable(pdf, { headers, rows, margin, pageW, pageH, y, textPrimary, textSecondary, ensureY }) {
  const colW = (pageW - 2 * margin) / headers.length;
  const drawHeader = () => {
    pdf.setFillColor(37, 99, 235);
    pdf.rect(margin, y, pageW - 2 * margin, 8, 'F');
    pdf.setFontSize(8);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(255, 255, 255);
    headers.forEach((h, i) => {
      pdf.text(String(h), margin + i * colW + 2, y + 5.5);
    });
    y += 10;
  };

  y = ensureY(y, 20);
  drawHeader();

  pdf.setFont(undefined, 'normal');
  rows.forEach((row) => {
    y = ensureY(y, 10);
    if (y < LAYOUT_MM.contentTop + 4) drawHeader();
    pdf.setFontSize(8);
    pdf.setTextColor(...textPrimary);
    row.forEach((cell, i) => {
      const lines = pdf.splitTextToSize(String(cell ?? '—'), colW - 3);
      pdf.text(lines[0] || '—', margin + i * colW + 2, y);
    });
    y += 6;
  });

  return y + 4;
}

async function generateMonthlyReportPdf(reportData, hospitalName, options = {}) {
  const overdueRoomDays = Number(options.overdueRoomDays) > 0 ? Number(options.overdueRoomDays) : 14;
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageW = pdf.internal.pageSize.getWidth();
  const margin = LAYOUT_MM.sideMargin;
  const contentBottom = LAYOUT_MM.contentBottom;
  const contentTop = LAYOUT_MM.contentTop;
  const pageH = contentBottom;
  const letterPadAssets = await getLetterPadAssetsDataUrls();
  const {
    brandPrimary,
    brandDark,
    successColor,
    warningColor,
    dangerColor,
    infoColor,
    textPrimary,
    textSecondary,
  } = createPdfPalette();

  const condColorPdf = (type) => {
    if (type === 'success' || type === 'win') return successColor;
    if (type === 'warning' || type === 'attention') return warningColor;
    if (type === 'danger' || type === 'error') return dangerColor;
    return infoColor;
  };

  const condBgPdf = (type) => {
    if (type === 'success' || type === 'win') return [236, 253, 245];
    if (type === 'warning' || type === 'attention') return [255, 251, 235];
    if (type === 'danger' || type === 'error') return [254, 242, 242];
    return [239, 246, 255];
  };

  const ensureY = (y, needed = 24) => {
    if (y > contentBottom - needed) {
      pdf.addPage();
      applyLetterPad(pdf, letterPadAssets);
      return contentTop;
    }
    return y;
  };

  applyLetterPad(pdf, letterPadAssets);

  let y = drawDocumentTitle(pdf, {
    title: 'Monthly Report',
    subtitle: `${hospitalName || 'Client'}  ·  ${reportData.month || 'Current Month'}`,
  });
  const kpis = reportData.kpis || {};
  const briefing = reportData.briefing || {};
  const highlights = briefing.highlights || [];
  const plan = reportData.plan || {};
  const overdueRooms = reportData.overdueRooms || [];
  const sessions = reportData.sessions || [];
  const rooms = reportData.rooms || [];
  const swabTests = (reportData.swabTests || []).map(normalizeSwabForExport);
  const tierCeiling = plan.tier ? (tierConfig[plan.tier]?.hours || 36) : 36;

  if (briefing.coverLetter) {
    pdf.setFillColor(241, 245, 249);
    pdf.roundedRect(margin, y, pageW - 2 * margin, 24, 3, 3, 'F');
    pdf.setDrawColor(...brandPrimary);
    pdf.setLineWidth(0.8);
    pdf.line(margin, y, margin, y + 24);
    pdf.setFontSize(9);
    pdf.setTextColor(...textPrimary);
    const coverLines = pdf.splitTextToSize(briefing.coverLetter, pageW - 2 * margin - 10);
    pdf.text(coverLines, margin + 6, y + 6);
    y += 30;
  }

  y = writePdfSectionTitle(pdf, 'Key Performance Indicators', margin, pageW, y, brandDark, brandPrimary);

  const kpiItems = [
    { label: 'UVC Hours', value: `${kpis.hoursThisMonth ?? '—'}h` },
    { label: 'Sessions', value: `${kpis.sessions ?? '—'}` },
    { label: 'Rooms', value: `${kpis.roomsCovered ?? '—'}` },
    { label: 'Tier Usage', value: `${kpis.tierUsagePct ?? '—'}%` },
    { label: 'Efficacy', value: `${kpis.efficacyPct ?? '—'}%` },
    { label: 'Object completion', value: `${kpis.avgObjectCompletionRate ?? '—'}%` },
    { label: 'Open follow-ups', value: `${kpis.openFollowUpCount ?? '—'}` },
    { label: 'Follow-up closure', value: `${kpis.followUpClosureRate ?? '—'}%` },
    { label: 'Pack coverage', value: `${kpis.packCoveragePct ?? '—'}%` },
    { label: 'Orphan cycles', value: `${kpis.orphanedCycleCount ?? '—'}` },
    { label: 'Packs under 2 min', value: `${kpis.packsUnder2MinPct ?? '—'}%` },
    { label: 'Follow-up SLA met', value: `${kpis.followUpSlaMetPct ?? '—'}%` },
    { label: 'Verification attach', value: `${kpis.verificationAttachRatePct ?? '—'}%` },
  ];
  const cols = 4;
  const gap = 4;
  const kpiW = (pageW - 2 * margin - (cols - 1) * gap) / cols;
  kpiItems.forEach((k, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const x = margin + col * (kpiW + gap);
    const rowY = y + row * 26;
    pdf.setFillColor(248, 250, 252);
    pdf.roundedRect(x, rowY, kpiW, 22, 3, 3, 'F');
    pdf.setFontSize(7);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(...textSecondary);
    pdf.text(k.label.toUpperCase(), x + 4, rowY + 7);
    pdf.setFontSize(13);
    pdf.setTextColor(...brandPrimary);
    pdf.text(k.value, x + 4, rowY + 17);
  });
  y += Math.ceil(kpiItems.length / cols) * 26 + 6;

  y = ensureY(y, 40);
  y = writePdfSectionTitle(pdf, 'Monthly Briefing', margin, pageW, y, brandDark, brandPrimary);

  if (briefing.narrative) {
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(...textPrimary);
    const briefLines = pdf.splitTextToSize(briefing.narrative, pageW - 2 * margin);
    pdf.text(briefLines, margin, y);
    y += briefLines.length * 5 + 4;
  } else {
    pdf.setFontSize(10);
    pdf.setTextColor(...textSecondary);
    pdf.text('No briefing data available.', margin, y);
    y += 8;
  }

  highlights.forEach((h, i) => {
    y = ensureY(y, 40);
    const bg = condBgPdf(h.type);
    const color = condColorPdf(h.type);
    const typeLabel = h.type === 'success' || h.type === 'win' ? 'WIN' : h.type === 'warning' || h.type === 'attention' ? 'ATTENTION' : h.type === 'info' ? 'INFO' : 'ALERT';

    pdf.setFillColor(...bg);
    const hlLines = pdf.splitTextToSize(h.text, pageW - 2 * margin - 16);
    const hlHeight = Math.max(16, hlLines.length * 5 + 10);
    pdf.roundedRect(margin, y, pageW - 2 * margin, hlHeight, 3, 3, 'F');
    pdf.setDrawColor(...color);
    pdf.setLineWidth(1);
    pdf.line(margin, y, margin, y + hlHeight);

    pdf.setFillColor(...color);
    pdf.circle(margin + 10, y + 8, 5, 'F');
    pdf.setFontSize(8);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(255, 255, 255);
    pdf.text(`${i + 1}`, margin + 10, y + 10, { align: 'center' });

    pdf.setFontSize(7);
    pdf.setTextColor(...color);
    pdf.text(typeLabel, margin + 18, y + 9);

    pdf.setFontSize(9);
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(...textPrimary);
    pdf.text(hlLines, margin + 8, y + hlHeight - hlLines.length * 5 + 2);
    y += hlHeight + 4;
  });

  y = ensureY(y, 50);
  y = writePdfSectionTitle(pdf, 'Subscription Status', margin, pageW, y, brandDark, brandPrimary);
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  pdf.setTextColor(...textPrimary);
  const planLines = [
    `Current Tier: ${plan.tier || '—'}`,
    `Hours Used / Ceiling: ${plan.hoursUsed ?? 0}h / ${tierCeiling}h`,
    `Renewal Date: ${plan.renewalDate || '—'}`,
    `Status: ${plan.status || '—'}`,
  ];
  planLines.forEach((line) => {
    pdf.text(line, margin, y);
    y += 6;
  });
  const usagePct = Math.min(100, ((plan.hoursUsed || 0) / tierCeiling) * 100);
  pdf.setFillColor(241, 245, 249);
  pdf.roundedRect(margin, y, pageW - 2 * margin, 5, 2, 2, 'F');
  const barColor = usagePct > 90 ? dangerColor : brandPrimary;
  pdf.setFillColor(...barColor);
  pdf.roundedRect(margin, y, ((pageW - 2 * margin) * usagePct) / 100, 5, 2, 2, 'F');
  y += 10;

  if (overdueRooms.length > 0) {
    y = ensureY(y, 40);
    y = writePdfSectionTitle(pdf, 'Rooms Requiring Attention', margin, pageW, y, brandDark, brandPrimary);
    y = writePdfTable(pdf, {
      headers: ['Room', 'Type', 'Days Since'],
      rows: overdueRooms.map((r) => [r.name, r.type || '—', `${r.daysSince ?? '—'} days`]),
      margin,
      pageW,
      pageH,
      y,
      textPrimary,
      textSecondary,
      ensureY,
    });
  }

  if (sessions.length > 0) {
    y = ensureY(y, 40);
    y = writePdfSectionTitle(
      pdf,
      `Session Log (Latest ${Math.min(sessions.length, 50)})`,
      margin,
      pageW,
      y,
      brandDark,
      brandPrimary
    );
    y = writePdfTable(pdf, {
      headers: ['Date', 'Room', 'Type', 'Duration', 'Method'],
      rows: sessions.slice(0, 50).map((s) => [s.date, s.room, s.type || '—', s.duration, s.method]),
      margin,
      pageW,
      pageH,
      y,
      textPrimary,
      textSecondary,
      ensureY,
    });
  }

  if (rooms.length > 0) {
    y = ensureY(y, 40);
    y = writePdfSectionTitle(pdf, 'Room Status Summary', margin, pageW, y, brandDark, brandPrimary);
    y = writePdfTable(pdf, {
      headers: ['Room', 'Type', 'Sessions', 'Days Since', 'Status'],
      rows: rooms.map((r) => {
        const isOverdue = r.daysSinceLastDisinfection > overdueRoomDays;
        return [
          r.name,
          r.type || '—',
          r.totalSessions ?? 0,
          r.daysSinceLastDisinfection ?? '—',
          isOverdue ? 'Overdue' : 'Healthy',
        ];
      }),
      margin,
      pageW,
      pageH,
      y,
      textPrimary,
      textSecondary,
      ensureY,
    });
  }

  if (swabTests.length > 0) {
    y = ensureY(y, 40);
    y = writePdfSectionTitle(
      pdf,
      'Environmental Swab Results (room-scoped)',
      margin,
      pageW,
      y,
      brandDark,
      brandPrimary
    );
    y = writePdfTable(pdf, {
      headers: ['Date', 'Room', 'Surface', 'Bact Pre', 'Bact Post', 'Fung Pre', 'Fung Post', 'Status'],
      rows: swabTests.map((t) => {
        const statusLabel =
          t.status === 'residual' ? EFFICACY_TERMS.postUvcPositive.reportsStatus : t.status;
        return [
          t.date,
          t.room,
          t.surface || '—',
          t.bacterialPre ?? '—',
          t.bacterialPost ?? '—',
          t.fungalPre ?? '—',
          t.fungalPost ?? '—',
          statusLabel,
        ];
      }),
      margin,
      pageW,
      pageH,
      y,
      textPrimary,
      textSecondary,
      ensureY,
    });
  }

  y = ensureY(y, 28);
  pdf.setFontSize(7);
  pdf.setFont(undefined, 'normal');
  pdf.setTextColor(...textSecondary);
  pdf.text(
    `Generated ${new Date().toLocaleDateString('en-GB')}  ·  Confidential — For ${hospitalName || 'Client'} internal use only`,
    pageW / 2,
    y,
    { align: 'center' }
  );
  y += 5;
  pdf.text(
    'UV-C is an adjunct to manual cleaning — supporting IPC evidence only; not a replacement for manual cleaning.',
    pageW / 2,
    y,
    { align: 'center' }
  );
  y += 5;
  pdf.text(
    'Documents disinfection activity only — does not claim NABH or JCI accreditation compliance or product endorsement.',
    pageW / 2,
    y,
    { align: 'center' }
  );

  const safeName = (hospitalName || 'report').replace(/[^a-zA-Z0-9_-]/g, '_');
  pdf.save(`Spotless_AI_Report_${safeName}_${(reportData.month || 'report').replace(/\s/g, '_')}.pdf`);
}

async function generateCertificatePdf({ hospitalName, hoursLabel, rangeLabel }) {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageW = pdf.internal.pageSize.getWidth();
  const { brandPrimary, textPrimary, textSecondary } = createPdfPalette();
  const letterPadAssets = await getLetterPadAssetsDataUrls();
  const contentBottom = LAYOUT_MM.contentBottom;
  const generatedAt = new Date().toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  applyLetterPad(pdf, letterPadAssets);

  let y = drawDocumentTitle(pdf, {
    title: 'CERTIFICATE OF SERVICE',
    subtitle: 'Documented UV-C disinfection service record',
  });

  pdf.setDrawColor(...brandPrimary);
  pdf.setLineWidth(0.4);
  pdf.line(pageW / 2 - 40, y, pageW / 2 + 40, y);
  y += 12;

  pdf.setFontSize(12);
  pdf.setFont(undefined, 'normal');
  pdf.setTextColor(...textPrimary);
  pdf.text(hospitalName || 'Client Hospital', pageW / 2, y, { align: 'center' });
  y += 18;

  pdf.setFontSize(28);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(...brandPrimary);
  pdf.text(hoursLabel, pageW / 2, y, { align: 'center' });
  y += 12;

  pdf.setFontSize(12);
  pdf.setFont(undefined, 'normal');
  pdf.setTextColor(...textSecondary);
  pdf.text(rangeLabel, pageW / 2, y, { align: 'center' });
  y += 14;

  pdf.setFontSize(10);
  pdf.text(`Generated ${generatedAt}`, pageW / 2, y, { align: 'center' });

  pdf.setFontSize(8);
  pdf.setTextColor(...textSecondary);
  const noteY = contentBottom - 24;
  pdf.text(
    `Confidential — For ${hospitalName || 'Client'} internal use only. For hospital audits and insurance documentation.`,
    pageW / 2,
    noteY,
    { align: 'center', maxWidth: pageW - 40 }
  );
  pdf.text(
    'UV-C is an adjunct to manual cleaning — supporting IPC evidence only.',
    pageW / 2,
    noteY + 8,
    { align: 'center', maxWidth: pageW - 40 }
  );
  pdf.text(
    'Records documented UV-C cycle hours only. Does not claim NABH or JCI accreditation compliance or product endorsement.',
    pageW / 2,
    noteY + 16,
    { align: 'center', maxWidth: pageW - 40 }
  );

  const safeName = (hospitalName || 'certificate').replace(/[^a-zA-Z0-9_-]/g, '_');
  pdf.save(`Spotless_AI_Certificate_${safeName}.pdf`);
}

async function generateSwabResultsPdf(reportData, hospitalName) {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageW = pdf.internal.pageSize.getWidth();
  const margin = LAYOUT_MM.sideMargin;
  const contentTop = LAYOUT_MM.contentTop;
  const contentBottom = LAYOUT_MM.contentBottom;
  const { textPrimary, textSecondary } = createPdfPalette();
  const letterPadAssets = await getLetterPadAssetsDataUrls();
  const swabTests = (reportData.swabTests || []).map(normalizeSwabForExport);

  const ensureY = (y, needed = 24) => {
    if (y > contentBottom - needed) {
      pdf.addPage();
      applyLetterPad(pdf, letterPadAssets);
      return contentTop;
    }
    return y;
  };

  applyLetterPad(pdf, letterPadAssets);

  let y = drawDocumentTitle(pdf, {
    title: 'Environmental Swab Results',
    subtitle: `${hospitalName || 'Client'}  ·  ${reportData.month || 'Current Month'}`,
    meta: 'Room-scoped environmental evidence — no patient identifiers',
  });

  y = writePdfTable(pdf, {
    headers: ['Date', 'Room', 'Surface', 'Bact Pre/Post', 'Fung Pre/Post', 'Status'],
    rows: swabTests.map((t) => {
      const statusLabel =
        t.status === 'residual' ? EFFICACY_TERMS.postUvcPositive.reportsStatus : t.status;
      return [
        t.date,
        t.room,
        t.surface || '—',
        `${t.bacterialPre ?? '—'} / ${t.bacterialPost ?? '—'}`,
        `${t.fungalPre ?? '—'} / ${t.fungalPost ?? '—'}`,
        statusLabel,
      ];
    }),
    margin,
    pageW,
    pageH: contentBottom,
    y,
    textPrimary,
    textSecondary,
    ensureY,
  });

  y = ensureY(typeof y === 'number' ? y : contentBottom - 20, 20);
  pdf.setFontSize(7);
  pdf.setTextColor(...textSecondary);
  pdf.text(
    `Generated ${new Date().toLocaleDateString('en-GB')}  ·  Confidential`,
    pageW / 2,
    y,
    { align: 'center' }
  );
  pdf.text(
    'UV-C is an adjunct to manual cleaning — supporting IPC evidence only.',
    pageW / 2,
    y + 5,
    { align: 'center' }
  );
  pdf.text(
    'Documents environmental disinfection evidence only — does not claim NABH or JCI accreditation compliance or product endorsement.',
    pageW / 2,
    y + 10,
    { align: 'center' }
  );

  const safeName = (hospitalName || 'swab').replace(/[^a-zA-Z0-9_-]/g, '_');
  pdf.save(`Spotless_AI_Swab_Results_${safeName}_${(reportData.month || 'report').replace(/\s/g, '_')}.pdf`);
}

export default function ReportsPage() {
  const { hospitalInfo, clientEmail, adminView, personaRole, clinicalRedacted } =
    useOutletContext() || {};
  const role = personaRole || normalizeHospitalRole(getUserRole());
  const canExport = !adminView && can(role, 'reports:export');
  const canWriteReports = !adminView && can(role, 'reports:write');
  const canWriteManualClean = !adminView && can(role, 'manual_clean:write');
  const canWriteVerification = !adminView && can(role, 'verification:write');
  const canSeeExceptions = adminView || can(role, 'exceptions:read');
  const canSeeFollowUps = adminView || can(role, 'followups:read');
  const canSeeCompliance = !adminView && can(role, 'compliance:read');
  const canWriteCompliance = !adminView && can(role, 'compliance:write');
  const canWriteExceptions = !adminView && can(role, 'exceptions:write');
  const canWriteFollowUps = !adminView && can(role, 'followups:write');
  const showSupportBanner = Boolean(adminView || clinicalRedacted);
  const [reports, setReports] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [generating, setGenerating] = useState({
    pdf: false,
    word: false,
    csv: false,
    swab: false,
    certificate: false,
    serverCsv: false,
  });
  const [showEarlier, setShowEarlier] = useState(false);
  const [cycles, setCycles] = useState([]);
  const [packsLoading, setPacksLoading] = useState(true);
  const [packsError, setPacksError] = useState('');
  const [exceptions, setExceptions] = useState([]);
  const [exceptionsLoading, setExceptionsLoading] = useState(true);
  const [exceptionsError, setExceptionsError] = useState('');
  const [followUps, setFollowUps] = useState([]);
  const [followUpsLoading, setFollowUpsLoading] = useState(true);
  const [followUpsError, setFollowUpsError] = useState('');
  const [drawerHistoryId, setDrawerHistoryId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [overdueRoomDays, setOverdueRoomDays] = useState(14);

  const email = clientEmail || getClientEmail();

  const fetchPacksAndExceptions = useCallback(async () => {
    if (!email) return;
    setPacksLoading(true);
    setExceptionsLoading(true);
    setFollowUpsLoading(true);
    setPacksError('');
    setExceptionsError('');
    setFollowUpsError('');
    try {
      const packsPromise = fetchEvidencePacks(email, { limit: 40 });
      const excPromise = canSeeExceptions
        ? fetchExceptions(email)
        : Promise.resolve({ success: true, exceptions: [] });
      const fuPromise = canSeeFollowUps
        ? fetchFollowUps(email)
        : Promise.resolve({ success: true, followUps: [] });
      const [packsData, excData, fuData] = await Promise.all([
        packsPromise,
        excPromise,
        fuPromise,
      ]);
      if (packsData?.success) setCycles(packsData.cycles || []);
      else setPacksError(packsData?.message || 'Unable to load evidence packs.');
      if (excData?.success) setExceptions(excData.exceptions || []);
      else setExceptionsError(excData?.message || 'Unable to load exceptions.');
      if (fuData?.success) setFollowUps(fuData.followUps || []);
      else setFollowUpsError(fuData?.message || 'Unable to load follow-ups.');
    } catch (e) {
      setPacksError(e?.response?.data?.message || 'Failed to load evidence packs.');
      setExceptionsError(e?.response?.data?.message || 'Failed to load exceptions.');
      setFollowUpsError(e?.response?.data?.message || 'Failed to load follow-ups.');
    } finally {
      setPacksLoading(false);
      setExceptionsLoading(false);
      setFollowUpsLoading(false);
    }
  }, [email, canSeeExceptions, canSeeFollowUps]);

  useEffect(() => {
    const loadReports = async () => {
      setLoading(true);
      setFetchError('');
      try {
        const [data, settingsData] = await Promise.all([
          fetchReportsApi(email),
          fetchComplianceSettings(email).catch(() => null),
        ]);
        if (settingsData?.success && settingsData.settings?.overdueRoomDays) {
          setOverdueRoomDays(Number(settingsData.settings.overdueRoomDays) || 14);
        }
        if (data?.success) {
          setReports(data.reports || []);
          setReportData(data.latestReport || null);
        } else {
          setReports([]);
          setReportData(null);
          setFetchError(data?.message || 'Unable to load reports.');
        }
      } catch (e) {
        console.error('Reports fetch error:', e);
        setReports([]);
        setReportData(null);
        setFetchError(e?.response?.data?.message || 'Failed to load reports. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    if (email) {
      loadReports();
      fetchPacksAndExceptions();
    }
  }, [email, fetchPacksAndExceptions]);

  const openPackDrawer = (historyId) => {
    setDrawerHistoryId(historyId);
    setDrawerOpen(true);
  };

  const handleServerCsvExport = async () => {
    if (!email) return;
    setGenerating((p) => ({ ...p, serverCsv: true }));
    try {
      const ym = reportData?.yearMonth;
      const qs = new URLSearchParams({ email });
      if (ym) qs.set('month', ym);
      const token = getAccessToken();
      const res = await fetch(
        `${API_BASE_URL}/client/reports/export/sessions.csv?${qs.toString()}`,
        {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      if (!res.ok) throw new Error('CSV export failed');
      const blob = await res.blob();
      downloadBlob(blob, `Spotless_Sessions_${ym || 'export'}.csv`);
    } catch (e) {
      console.error(e);
    } finally {
      setGenerating((p) => ({ ...p, serverCsv: false }));
    }
  };

  const monthlyRows = useMemo(() => {
    const rows = (reports || []).map(mapApiReportRow);
    if (rows.length === 0 && reportData?.month) {
      const kpis = reportData.kpis || {};
      return [
        {
          id: reportData.month,
          month: reportData.month,
          title: `${reportData.month} Performance Report`,
          meta: formatReportMeta(kpis),
          tag: null,
          pdfUrl: reportData.pdfUrl || null,
          isLatest: true,
        },
      ];
    }
    if (rows.length > 0 && reportData?.month) {
      const kpis = reportData.kpis || {};
      const latestIdx = rows.findIndex(
        (r) => r.isLatest || r.month === reportData.month || r.title?.includes(reportData.month)
      );
      const idx = latestIdx >= 0 ? latestIdx : 0;
      rows[idx] = {
        ...rows[idx],
        title: rows[idx].title || `${reportData.month} Performance Report`,
        meta: rows[idx].meta && !rows[idx].meta.includes('—') ? rows[idx].meta : formatReportMeta(kpis),
        isLatest: true,
      };
    }
    return rows;
  }, [reports, reportData]);

  const visibleRows = useMemo(() => {
    if (showEarlier || monthlyRows.length <= 5) return monthlyRows;
    return monthlyRows.slice(0, 5);
  }, [monthlyRows, showEarlier]);

  const earlierReportsCount = monthlyRows.length > 5 ? monthlyRows.length - 5 : 0;

  const certificateStats = useMemo(() => {
    const kpis = reportData?.kpis || {};
    const summary = reportData?.summary || {};
    const hoursRaw =
      kpis.hoursThisMonth ??
      reportData?.verifiedHours ??
      kpis.verifiedHours ??
      null;
    const hours = Number(hoursRaw);

    let rangeLabel = reportData?.month || '—';
    if (reportData?.periodStart && reportData?.periodEnd) {
      rangeLabel = `${reportData.periodStart} — ${reportData.periodEnd}`;
    } else if (reportData?.yearMonth) {
      rangeLabel = reportData.month || reportData.yearMonth;
    }

    const packHint =
      summary.packCount != null
        ? ` · ${summary.signedPackCount ?? 0}/${summary.packCount} packs signed`
        : '';

    const hoursDisplay =
      Number.isFinite(hours) && hours >= 0
        ? `${Number(hours.toFixed(1))} hours documented${packHint}`
        : hoursRaw != null
          ? `${hoursRaw} hours documented${packHint}`
          : '— hours documented';

    return { hoursLabel: hoursDisplay, rangeLabel };
  }, [reportData]);

  const hospitalName = hospitalInfo?.name || 'Client Hospital';
  const hasSessions = (reportData?.sessions || []).length > 0;
  const hasSwabs = (reportData?.swabTests || []).length > 0;

  const handleDownloadWord = () => {
    if (!reportData) return;
    setGenerating((p) => ({ ...p, word: true }));
    try {
      const htmlContent = buildWordHtml(reportData, hospitalName, { overdueRoomDays });
      const blob = new Blob([htmlContent], { type: 'application/msword' });
      downloadBlob(
        blob,
        `Spotless_AI_Monthly_Report_${(reportData.month || 'report').replace(/\s/g, '_')}.doc`
      );
    } finally {
      setGenerating((p) => ({ ...p, word: false }));
    }
  };

  const handleDownloadPdf = async () => {
    if (!reportData) return;
    setGenerating((p) => ({ ...p, pdf: true }));
    try {
      await generateMonthlyReportPdf(reportData, hospitalName, { overdueRoomDays });
    } catch (err) {
      console.error('PDF generation error:', err);
    } finally {
      setGenerating((p) => ({ ...p, pdf: false }));
    }
  };

  const handleDownloadCertificate = async () => {
    if (!reportData && !reports.length) return;
    setGenerating((p) => ({ ...p, certificate: true }));
    try {
      await generateCertificatePdf({
        hospitalName,
        hoursLabel: certificateStats.hoursLabel,
        rangeLabel: certificateStats.rangeLabel,
      });
    } catch (err) {
      console.error('Certificate PDF error:', err);
    } finally {
      setGenerating((p) => ({ ...p, certificate: false }));
    }
  };

  const handleExportSessionCsv = () => {
    if (!reportData?.sessions?.length) return;
    setGenerating((p) => ({ ...p, csv: true }));
    try {
      const headers = ['Date', 'Room', 'Type', 'Duration', 'Method'];
      const rows = reportData.sessions.map((s) =>
        [s.date, s.room, s.type || '', s.duration || '', s.method || ''].map(csvEscape)
      );
      const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      downloadBlob(
        blob,
        `Spotless_AI_Session_Log_${(reportData.month || 'report').replace(/\s/g, '_')}.csv`
      );
    } finally {
      setGenerating((p) => ({ ...p, csv: false }));
    }
  };

  const handleExportSwabPdf = async () => {
    if (!reportData?.swabTests?.length) return;
    setGenerating((p) => ({ ...p, swab: true }));
    try {
      await generateSwabResultsPdf(reportData, hospitalName);
    } catch (err) {
      console.error('Swab PDF error:', err);
    } finally {
      setGenerating((p) => ({ ...p, swab: false }));
    }
  };

  const canUseClientPdf = Boolean(reportData);
  const isLatestRow = (row) =>
    row.isLatest || (reportData?.month && (row.month === reportData.month || row.title?.includes(reportData.month)));

  const handleRowPreview = (row) => {
    if (row.pdfUrl) {
      window.open(row.pdfUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (isLatestRow(row) && reportData) handleDownloadPdf();
  };

  const handleRowDownload = (row) => {
    if (row.pdfUrl) {
      window.open(row.pdfUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (isLatestRow(row) && reportData) handleDownloadPdf();
  };

  const openExceptions = exceptions.filter((ex) => ex.status === 'open' || ex.status === 'acknowledged').length;
  const openFollowUps =
    reportData?.summary?.openFollowUpCount ??
    followUps.filter((t) => t.status === 'open' || t.status === 'acknowledged').length;
  const signedPacks = cycles.filter((c) => c.signed).length;
  const packCount = reportData?.summary?.packCount ?? cycles.length;
  const kpis = reportData?.kpis || {};

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          py: 12,
          mx: { xs: -2, md: -3 },
          mt: { xs: -2, md: -3 },
          minHeight: 360,
          background: `
            radial-gradient(900px 400px at 12% 0%, rgba(59, 130, 246, 0.08), transparent 55%),
            radial-gradient(700px 320px at 88% 8%, rgba(236, 72, 153, 0.06), transparent 50%),
            ${COLORS.pageCanvas}
          `,
        }}
      >
        <CircularProgress sx={{ color: COLORS.brandPrimary }} />
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.textSecondary }}>
          Loading reports…
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mx: { xs: -2, md: -3 },
        mt: { xs: -2, md: -3 },
        mb: { xs: -2, md: -3 },
        px: { xs: 2, md: 3 },
        py: { xs: 2.5, md: 3.5 },
        minHeight: '100%',
        background: `
          radial-gradient(1100px 480px at 8% -10%, rgba(59, 130, 246, 0.09), transparent 55%),
          radial-gradient(900px 420px at 96% 0%, rgba(236, 72, 153, 0.07), transparent 50%),
          radial-gradient(700px 360px at 50% 100%, rgba(5, 150, 105, 0.05), transparent 45%),
          ${COLORS.pageCanvas}
        `,
        '@keyframes reportsFadeUp': {
          from: { opacity: 0, transform: 'translateY(12px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {/* Page header */}
      <Box
        sx={{
          mb: 3,
          animation: 'reportsFadeUp 0.4s ease-out both',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography
              sx={{
                fontSize: { xs: 26, md: 30 },
                fontWeight: 800,
                letterSpacing: '-0.03em',
                color: COLORS.textPrimary,
                lineHeight: 1.15,
              }}
            >
              Reports
            </Typography>
            <Typography sx={{ fontSize: 14, color: COLORS.textSecondary, mt: 0.75, maxWidth: 640, lineHeight: 1.5 }}>
              Per-cycle disinfection evidence packs, hygiene exception triage, and monthly performance
              exports for{' '}
              <Box component="span" sx={{ fontWeight: 700, color: COLORS.textPrimary }}>
                {hospitalName}
              </Box>
              . UV exposure shown as runtime proxy (not mJ/cm²). Room and turnover scoped only — not a
              patient record, HAI surveillance, or accreditation management system. Obligation anchors
              document organisational duties; they do not claim NABH or JCI compliance.
            </Typography>
            {showSupportBanner ? (
              <Alert severity="info" sx={{ mt: 1.5, maxWidth: 640 }}>
                Support view — clinical evidence (verifications, swabs, photos) is hidden by default.
              </Alert>
            ) : null}
          </Box>
          {reportData?.month ? (
            <Chip
              icon={<MdAssessment size={16} />}
              label={reportData.month}
              sx={{
                height: 32,
                fontWeight: 600,
                bgcolor: '#fff',
                border: `1px solid ${COLORS.borderDefault}`,
                '& .MuiChip-icon': { color: COLORS.brandPrimary },
              }}
            />
          ) : null}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2.5 }}>
          <ReportStat
            icon={<MdFolderSpecial size={20} color={COLORS.brandPrimary} />}
            label="Evidence packs"
            value={packCount}
            accent="rgba(59, 130, 246, 0.1)"
          />
          <ReportStat
            icon={<MdVerifiedUser size={20} color={COLORS.accentSuccess} />}
            label="Signed"
            value={reportData?.summary?.signedPackCount ?? signedPacks}
            accent="rgba(5, 150, 105, 0.12)"
          />
          <ReportStat
            icon={<MdWarningAmber size={20} color={openExceptions > 0 ? COLORS.accentWarning : COLORS.accentSuccess} />}
            label="Open exceptions"
            value={reportData?.summary?.openExceptionCount ?? openExceptions}
            accent={openExceptions > 0 ? 'rgba(217, 119, 6, 0.12)' : 'rgba(5, 150, 105, 0.12)'}
          />
          <ReportStat
            icon={<MdAccessTime size={20} color={COLORS.brandAccent} />}
            label="UVC this month"
            value={kpis.hoursThisMonth != null ? `${kpis.hoursThisMonth}h` : '—'}
            accent="rgba(236, 72, 153, 0.1)"
          />
          <ReportStat
            icon={<MdAssessment size={20} color={COLORS.brandPrimary} />}
            label="Object completion"
            value={kpis.avgObjectCompletionRate != null ? `${kpis.avgObjectCompletionRate}%` : '—'}
            accent="rgba(59, 130, 246, 0.1)"
          />
          <ReportStat
            icon={<MdWarningAmber size={20} color={openFollowUps > 0 ? COLORS.accentWarning : COLORS.accentSuccess} />}
            label="Open follow-ups"
            value={openFollowUps}
            accent={openFollowUps > 0 ? 'rgba(217, 119, 6, 0.12)' : 'rgba(5, 150, 105, 0.12)'}
          />
          <ReportStat
            icon={<MdFolderSpecial size={20} color={COLORS.brandPrimary} />}
            label="Pack coverage"
            value={kpis.packCoveragePct != null ? `${kpis.packCoveragePct}%` : '—'}
            accent="rgba(59, 130, 246, 0.1)"
          />
          <ReportStat
            icon={
              <MdWarningAmber
                size={20}
                color={
                  (kpis.orphanedCycleCount || 0) > 0 ? COLORS.accentWarning : COLORS.accentSuccess
                }
              />
            }
            label="Orphan cycles (30d)"
            value={kpis.orphanedCycleCount ?? '—'}
            accent={
              (kpis.orphanedCycleCount || 0) > 0
                ? 'rgba(217, 119, 6, 0.12)'
                : 'rgba(5, 150, 105, 0.12)'
            }
          />
          <ReportStat
            icon={<MdAccessTime size={20} color={COLORS.brandPrimary} />}
            label="Packs under 2 min"
            value={kpis.packsUnder2MinPct != null ? `${kpis.packsUnder2MinPct}%` : '—'}
            accent="rgba(59, 130, 246, 0.1)"
          />
          <ReportStat
            icon={<MdVerifiedUser size={20} color={COLORS.accentSuccess} />}
            label="Follow-up SLA met"
            value={kpis.followUpSlaMetPct != null ? `${kpis.followUpSlaMetPct}%` : '—'}
            accent="rgba(5, 150, 105, 0.12)"
          />
          <ReportStat
            icon={<MdAssessment size={20} color={COLORS.brandAccent} />}
            label="Verification attach"
            value={
              kpis.verificationAttachRatePct != null
                ? `${kpis.verificationAttachRatePct}%`
                : '—'
            }
            accent="rgba(236, 72, 153, 0.1)"
          />
        </Box>
      </Box>

      <EvidencePacksPanel
        cycles={cycles}
        loading={packsLoading}
        error={packsError}
        email={email}
        onView={openPackDrawer}
        onSign={openPackDrawer}
        onRefresh={fetchPacksAndExceptions}
        canExport={canExport}
        canSign={canWriteReports}
      />

      {canSeeExceptions ? (
        <Box sx={{ mb: 3 }}>
          <ExceptionQueuePanel
            exceptions={exceptions}
            loading={exceptionsLoading}
            error={exceptionsError}
            email={email}
            onChanged={canWriteExceptions ? fetchPacksAndExceptions : undefined}
            onOpenPack={openPackDrawer}
            readOnly={!canWriteExceptions}
          />
        </Box>
      ) : null}

      {canSeeFollowUps ? (
        <FollowUpQueuePanel
          followUps={followUps}
          loading={followUpsLoading}
          error={followUpsError}
          email={email}
          onChanged={canWriteFollowUps ? fetchPacksAndExceptions : undefined}
          onOpenPack={openPackDrawer}
          readOnly={!canWriteFollowUps}
        />
      ) : null}

      {canSeeCompliance ? (
        <Box sx={{ my: 3 }}>
          <ComplianceSettingsPanel
            email={email}
            adminView={Boolean(adminView) || !canWriteCompliance}
          />
        </Box>
      ) : null}

      <Grid container spacing={3} sx={{ animation: 'reportsFadeUp 0.5s ease-out 0.1s both' }}>
        <Grid item xs={12} lg={8}>
          <Paper elevation={0} sx={surfaceCardSx}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
              <Box>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, letterSpacing: '-0.02em' }}>
                  Monthly performance
                </Typography>
                <Typography sx={{ fontSize: 13, color: COLORS.textSecondary, mt: 0.5, maxWidth: 480, lineHeight: 1.45 }}>
                  Facility-level monthly reports — secondary to per-cycle evidence packs.
                </Typography>
              </Box>
            </Box>

            {fetchError ? (
              <Alert severity="error" sx={{ mb: 2, borderRadius: `${theme.radius.md}px` }}>
                {fetchError}
              </Alert>
            ) : null}

            {!fetchError && monthlyRows.length === 0 ? (
              <Box
                sx={{
                  py: 6,
                  textAlign: 'center',
                  borderRadius: `${theme.radius.md}px`,
                  bgcolor: COLORS.bgSecondary,
                  border: `1px dashed ${COLORS.borderDefault}`,
                }}
              >
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: COLORS.textPrimary }}>
                  No reports yet
                </Typography>
                <Typography sx={{ fontSize: 13, color: COLORS.textSecondary, mt: 1 }}>
                  Monthly reports will appear here once disinfection activity is available for this facility.
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {visibleRows.map((row, index) => {
                    const canClientGen = isLatestRow(row) && canUseClientPdf;
                    const canOpen = Boolean(row.pdfUrl) || canClientGen;
                    const busy = canClientGen && generating.pdf;
                    const latest = isLatestRow(row);
                    return (
                      <Paper
                        key={row.id}
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: `${theme.radius.md}px`,
                          border: `1px solid ${latest ? 'rgba(59, 130, 246, 0.28)' : COLORS.borderDefault}`,
                          bgcolor: latest ? '#f8fbff' : COLORS.bgSecondary,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          flexWrap: { xs: 'wrap', sm: 'nowrap' },
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                          animation: `reportsFadeUp 0.35s ease-out ${Math.min(index, 6) * 0.04}s both`,
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: '0 6px 18px rgba(15, 23, 42, 0.07)',
                            bgcolor: '#fff',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 48,
                            height: 56,
                            borderRadius: `${theme.radius.md}px`,
                            background: latest
                              ? 'linear-gradient(145deg, rgba(59,130,246,0.14), rgba(236,72,153,0.1))'
                              : COLORS.bgTertiary,
                            border: `1px solid ${COLORS.borderDefault}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            flexShrink: 0,
                          }}
                        >
                          <MdPictureAsPdf size={22} color={latest ? COLORS.brandPrimary : COLORS.textSecondary} />
                          <Typography
                            sx={{
                              fontSize: 9,
                              fontWeight: 700,
                              color: latest ? COLORS.brandPrimary : COLORS.textSecondary,
                              mt: 0.25,
                            }}
                          >
                            PDF
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 700, color: COLORS.textPrimary }}>
                              {row.title}
                            </Typography>
                            {latest ? (
                              <Chip
                                size="small"
                                label="Latest"
                                sx={{
                                  height: 20,
                                  fontSize: 10,
                                  fontWeight: 700,
                                  bgcolor: 'rgba(59, 130, 246, 0.12)',
                                  color: COLORS.brandPrimary,
                                }}
                              />
                            ) : null}
                          </Box>
                          <Typography sx={{ fontSize: 13, color: COLORS.textSecondary, mt: 0.25 }}>
                            {row.meta}
                            {row.tag ? (
                              <Box component="span" sx={{ color: COLORS.textSecondary }}>
                                {' '}
                                · {row.tag}
                              </Box>
                            ) : null}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'row', sm: 'column' },
                            gap: 1,
                            width: { xs: '100%', sm: 120 },
                            flexShrink: 0,
                          }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<MdVisibility size={16} />}
                            onClick={() => handleRowPreview(row)}
                            disabled={!canOpen || busy}
                            sx={{ ...outlineBtnSx, width: '100%' }}
                          >
                            Preview
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={
                              busy ? (
                                <CircularProgress size={14} sx={{ color: COLORS.textSecondary }} />
                              ) : (
                                <MdDownload size={16} />
                              )
                            }
                            onClick={() => handleRowDownload(row)}
                            disabled={!canOpen || busy}
                            sx={{ ...outlineBtnSx, width: '100%' }}
                          >
                            {busy ? 'Generating…' : 'Download'}
                          </Button>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>

                {earlierReportsCount > 0 ? (
                  <Box sx={{ textAlign: 'center', mt: 2.5 }}>
                    <Button sx={ghostLinkBtnSx} onClick={() => setShowEarlier((v) => !v)}>
                      {showEarlier ? 'Show fewer' : `+ ${earlierReportsCount} earlier reports`}
                    </Button>
                  </Box>
                ) : null}
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Paper elevation={0} sx={surfaceCardSx}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, letterSpacing: '-0.02em' }}>
                Service certificate
              </Typography>
              <Typography sx={{ fontSize: 13, color: COLORS.textSecondary, mt: 0.5, mb: 2, lineHeight: 1.45 }}>
                Documented UV-C hours for hospital audits and insurance documentation. Standards cited
                on packs are obligation anchors only — not product endorsements or accreditation claims.
              </Typography>
              <Box
                sx={{
                  background: 'linear-gradient(160deg, #ecfdf5 0%, #f0fdfa 45%, #ffffff 100%)',
                  borderRadius: `${theme.radius.md}px`,
                  p: 2.5,
                  border: '1px solid rgba(5, 150, 105, 0.18)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'rgba(5, 150, 105, 0.08)',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    color: COLORS.accentSuccess,
                  }}
                >
                  CERTIFICATE OF SERVICE
                </Typography>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: COLORS.textPrimary,
                    my: 1,
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {certificateStats.hoursLabel}
                </Typography>
                <Typography sx={{ fontSize: 12, color: COLORS.textSecondary }}>
                  {certificateStats.rangeLabel}
                </Typography>
              </Box>
              <Button
                fullWidth
                variant="contained"
                endIcon={generating.certificate ? <CircularProgress size={14} color="inherit" /> : <MdOpenInNew size={16} />}
                onClick={handleDownloadCertificate}
                disabled={!canExport || (!reportData && !reports.length) || generating.certificate}
                sx={{
                  mt: 2,
                  py: 1.25,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: `${theme.radius.md}px`,
                  background: theme.gradients.primary,
                  boxShadow: '0 4px 14px rgba(59, 130, 246, 0.25)',
                  '&:hover': {
                    background: theme.gradients.primaryHover,
                    boxShadow: '0 6px 18px rgba(59, 130, 246, 0.3)',
                  },
                  '&.Mui-disabled': { bgcolor: COLORS.bgTertiary, color: COLORS.textSecondary },
                }}
              >
                {generating.certificate ? 'Generating…' : 'Generate certificate'}
              </Button>
            </Paper>

            <Paper elevation={0} sx={surfaceCardSx}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, mb: 2, letterSpacing: '-0.02em' }}>
                Quick exports
              </Typography>
              <Typography sx={{ fontSize: 12, color: COLORS.textSecondary, mb: 1.5, lineHeight: 1.45 }}>
                Live PDF, Word, and CSV from this facility&apos;s report data. Share links, email
                schedules, SFTP/webhook drops, and media ZIP archives are not available yet and are
                not simulated here.
              </Typography>
              {!canExport ? (
                <Typography sx={{ fontSize: 13, color: COLORS.textSecondary }}>
                  Export is not available for your role.
                </Typography>
              ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={generating.serverCsv}
                  onClick={handleServerCsvExport}
                  startIcon={generating.serverCsv ? <CircularProgress size={14} /> : <MdDownload size={18} />}
                  sx={exportBtnSx}
                >
                  Session log · CSV (server)
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={!hasSessions || generating.csv}
                  onClick={handleExportSessionCsv}
                  startIcon={generating.csv ? <CircularProgress size={14} /> : <MdDownload size={18} />}
                  sx={exportBtnSx}
                >
                  Session log · CSV
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={!reportData || generating.word}
                  onClick={handleDownloadWord}
                  startIcon={generating.word ? <CircularProgress size={14} /> : <MdDescription size={18} />}
                  sx={exportBtnSx}
                >
                  Monthly report · Word
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={!reportData || generating.pdf}
                  onClick={handleDownloadPdf}
                  startIcon={generating.pdf ? <CircularProgress size={14} /> : <MdPictureAsPdf size={18} />}
                  sx={exportBtnSx}
                >
                  Monthly report · PDF
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={!hasSwabs || generating.swab}
                  onClick={handleExportSwabPdf}
                  startIcon={generating.swab ? <CircularProgress size={14} /> : <MdPictureAsPdf size={18} />}
                  sx={exportBtnSx}
                >
                  Swab results · PDF (room-scoped)
                </Button>
              </Box>
              )}
            </Paper>
          </Box>
        </Grid>
      </Grid>

      <EvidencePackDetailDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        historyId={drawerHistoryId}
        email={email}
        onUpdated={fetchPacksAndExceptions}
        adminView={Boolean(adminView)}
        canWriteReports={canWriteReports}
        canExport={canExport}
        canWriteManualClean={canWriteManualClean}
        canWriteVerification={canWriteVerification}
        canWriteFollowUps={canWriteFollowUps}
      />
    </Box>
  );
}
